<template>
  <button
    class="btn btn-warning btn-sm"
    data-testid="pdf-export-btn"
    @click="handlePdfExport"
    :disabled="loading">
    <ProgressSpinner v-if="loading" style="width: 1rem; height: 1rem" strokeWidth="4" />
    <i v-else class="pi pi-file-pdf"></i> 
    {{ text || 'pdf' }}
  </button>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressSpinner from 'primevue/progressspinner';
import { useDateFormatter } from '../../composables/useDateFormatter';

// Type declarations for jsPDF global object
declare global {
  interface Window {
    jsPDF?: any;
    jspdf?: {
      jsPDF?: any;
    };
  }
}

const props = defineProps<{
  text?: string;
  config: Record<string, any>;
  data?: any[];
  getFilteredData?: () => any[];
}>();

const emit = defineEmits<{
  (e: 'pdf-export'): void;
  (e: 'pdf-error', error: any): void;
}>();

// Get i18n for formatting
const { n, d, locale } = useI18n();
const { formatDate, formatDateTime } = useDateFormatter();

const loading = ref(false);

const handlePdfExport = async () => {
  if (loading.value) return;
  
  try {
    loading.value = true;
    
    await generatePdf();
    
    // Emit success event
    emit('pdf-export');
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    emit('pdf-error', error);
  } finally {
    loading.value = false;
  }
};

const generatePdf = async () => {
  try {
    // Load jsPDF if not already loaded
    await loadJsPDF();
    
    if (!window.jsPDF && !window.jspdf) {
      throw new Error('jsPDF library not available after loading');
    }
    
    // Try to get jsPDF constructor from different possible global names
    let jsPDF;
    if (window.jsPDF) {
      jsPDF = window.jsPDF;
    } else if (window.jspdf && window.jspdf.jsPDF) {
      jsPDF = window.jspdf.jsPDF;
    } else if (window.jspdf) {
      jsPDF = window.jspdf;
    }
    
    if (!jsPDF) {
      throw new Error('jsPDF constructor not available');
    }
    
    const doc = new jsPDF('l', 'mm', 'a4'); // landscape, millimeters, A4
    
    // Get filtered data or all data
    const filtered = props.getFilteredData?.() ?? [];
    const dataToExport = filtered.length > 0 ? filtered : (props.data ?? []);
    
    if (!dataToExport || dataToExport.length === 0) {
      throw new Error('No data to export');
    }
    
    // PDF settings
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const tableStartY = 20;
    const rowHeight = 8;
    const headerHeight = 10;
    
    // Calculate column widths
    const columns = props.config.columns.filter((col: any) => col.visible !== false);
    const colWidth = (pageWidth - 2 * margin) / columns.length;
    
    let currentY = tableStartY;
    let pageNumber = 1;
    
    // Add title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const title = props.config.title || 'Relatório';
    doc.text(title, margin, 15);
    
    // Add date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const currentDate = formatDate(new Date());
    doc.text(`Gerado em: ${currentDate}`, pageWidth - margin - 50, 15);
    
    // Add header row
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    let xPos = margin;

    columns.forEach((column: any, index: any) => {
      const text = column.title || column.data;
      doc.text(text, xPos, currentY);
      xPos += colWidth;
    });
    
    currentY += headerHeight;
    
    // Add data rows
    doc.setFont('helvetica', 'normal');
    
    dataToExport.forEach((row, rowIndex) => {
      // Check if we need a new page
      if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage();
        pageNumber++;
        currentY = tableStartY;
        
        // Add header row on new page
        doc.setFont('helvetica', 'bold');
        xPos = margin;
        columns.forEach((column: any) => {
          const text = column.title || column.data;
          doc.text(text, xPos, currentY);
          xPos += colWidth;
        });
        currentY += headerHeight;
        doc.setFont('helvetica', 'normal');
      }
      
      // Add data row
      xPos = margin;
      columns.forEach((column: any, colIndex: any) => {
        let cellValue = row[column.data];
        
        // Format cell value based on render type
        if (column.renderType) {
          cellValue = formatCellValueForPdf(cellValue, column.renderType);
        }
        
        // Convert to string and truncate if too long
        const cellText = String(cellValue || '').substring(0, 30);
        
        doc.text(cellText, xPos, currentY);
        xPos += colWidth;
      });
      
      currentY += rowHeight;
    });
    
    // Add footer with page numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin - 30, pageHeight - 10);
    }
    
    // Generate filename with .pdf extension
    const baseFilename = props.config.filename || `relatorio_${new Date().toISOString().split('T')[0]}`;
    const filename = baseFilename.endsWith('.pdf') ? baseFilename : `${baseFilename}.pdf`;
    
    // Save the PDF
    doc.save(filename);
    
  } catch (error) {
    console.error('PDF Generation Error:', error);
    console.error('window.jsPDF available:', !!window.jsPDF);
    console.error('window.jspdf available:', !!window.jspdf);
    console.error('window.jsPDF type:', typeof window.jsPDF);
    console.error('window.jspdf type:', typeof window.jspdf);
    if (window.jspdf) {
      console.error('window.jspdf.jsPDF available:', !!window.jspdf.jsPDF);
    }
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};

const loadJsPDF = () => {
  // Check if jsPDF is already loaded (try different possible global names)
  if (window.jsPDF || window.jspdf) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    // Set a timeout to prevent hanging
    const timeout = setTimeout(() => {
      reject(new Error('Timeout loading jsPDF library'));
    }, 10000); // 10 second timeout
    
    // Try multiple CDN sources for better reliability
    const cdnSources = [
      'https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
      'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js'
    ];
    
    let currentIndex = 0;
    
    const tryLoadScript = () => {
      if (currentIndex >= cdnSources.length) {
        clearTimeout(timeout);
        reject(new Error('Failed to load jsPDF from all CDN sources'));
        return;
      }
      
      const script = document.createElement('script');
      script.src = cdnSources[currentIndex];
      script.onload = () => {
        // Verify jsPDF is actually available (check both possible global names)
        if (window.jsPDF || window.jspdf) {
          clearTimeout(timeout);
          resolve(undefined);
        } else {
          currentIndex++;
          tryLoadScript();
        }
      };
      script.onerror = () => {
        currentIndex++;
        tryLoadScript();
      };
      document.head.appendChild(script);
    };
    
    tryLoadScript();
  });
};

const formatCellValueForPdf = (value: any, renderType: any) => {
  if (value === null || value === undefined) return '';

  switch (renderType) {
    case 'currency':
      const currencyValue = parseFloat(value) || 0;
      return n(currencyValue, 'currency');
    case 'number':
      const numberValue = parseFloat(value) || 0;
      return n(numberValue, 'decimal');
    case 'percentage':
      const percentageValue = parseFloat(value) || 0;
      return n(percentageValue / 100, 'percent');
    case 'date':
      if (!value) return '';
      const date = new Date(value);
      return formatDate(date);
    case 'datetime':
      if (!value) return '';
      const datetime = new Date(value);
      return formatDateTime(datetime);
    case 'yes_no':
      return value ? 'Sim' : 'Não';
    case 'active_inactive':
      return value ? 'Ativo' : 'Inativo';
    default:
      return String(value);
  }
};
</script>
